/**
 * docxToPlate — convert an uploaded `.docx` file into a Plate fragment.
 *
 * Pipeline (mirrors the editor's existing Word-HTML PASTE path so imported and
 * pasted Word content behave identically):
 *
 *   .docx (zip)  ──mammoth (styleMap + alignment transform)──▶  Word HTML
 *        ──sanitizeOfficeHtml──▶  clean HTML  ──strip data-slate-*──▶
 *        ──marker classes → inline styles──▶  ──list/align paste prep──▶
 *        ──deserializeHtmlElement(editor)──▶  Plate fragment
 *        ──fixInvalidShapes──▶  ──applyOoxmlFidelity (raw XML re-attach)──▶
 *        ──convertLayoutTablesToColumns──▶  ──trimTrailingEmptyParagraphs──▶
 *        final fragment
 *
 * Why mammoth: a `.docx` is a zipped OOXML bundle, not HTML. mammoth unzips it
 * and emits semantic HTML (headings, paragraphs, lists, tables, bold/italic,
 * inline images as base64 data URLs) — exactly the shape the editor's HTML
 * deserializer already understands. We deliberately reuse `sanitizeOfficeHtml`
 * + `deserializeHtmlElement` rather than a bespoke OOXML parser so there is ONE
 * Word-ingestion code path to maintain.
 *
 * Images: mammoth inlines images as `data:` URLs by default; the deserializer
 * turns those into Plate image nodes, and the surface's existing
 * save pipeline (processImagesBeforeSave) uploads them on save — same as a
 * pasted data-URL image.
 *
 * Post-processing (fixInvalidShapes): defensive pass over the deserialized
 * fragment that fixes any invalid node types that survive the HTML path:
 *   - type:'img' → type:'image' with void marker child (should not occur since
 *     ImagePlugin.parsers.html handles <img>, but defensive against edge cases)
 *   - h4/h5/h6 → h3 (only h1-h3 registered; Plate's HTML deserializer already
 *     falls back for unregistered HTML tags, but custom styles can produce them)
 */
import { collapseWhiteSpace, deserializeHtmlElement } from 'platejs';
import mammoth from 'mammoth';
import { sanitizeOfficeHtml } from './sanitizeOfficeHtml';
import { convertLayoutTablesToColumns } from './layoutTables';
import { enrichPastedAlign, enrichPastedListElements, flattenPastedListItems } from './pasteNormalize';
import { applyOoxmlFidelity } from './ooxml';
import {
    DOCX_ALIGN_CLASS_PREFIX,
    DOCX_ALIGN_STYLE_NAME_PREFIX,
    DOCX_HIGHLIGHT_CLASS_PREFIX,
    DOCX_HIGHLIGHT_HEX,
    DOCX_IMPORT_STYLE_MAP,
    DOCX_JC_TO_TEXT_ALIGN,
} from './docxImportConstants';

// Defensive post-processor: fixes invalid node types that could survive the
// HTML deserialization path. Walks the fragment tree recursively.
function fixInvalidShapes(nodes: any[]): any[] {
    const out: any[] = [];
    for (const node of nodes) {
        if (node == null) continue;
        if (typeof node.text === 'string') {
            out.push(node);
            continue;
        }

        const type: string | undefined = node.type;

        // type:'img' must not reach the editor — ImagePlugin uses key 'image'.
        // The HTML path should produce 'image' via ImagePlugin.parsers.html, but
        // guard here against any edge case that bypasses it.
        if (type === 'img') {
            out.push({ ...node, type: 'image', children: [{ text: '' }] });
            continue;
        }

        // h4/h5/h6 → h3 (only h1-h3 registered in the editor).
        if (type === 'h4' || type === 'h5' || type === 'h6') {
            const children = Array.isArray(node.children) ? fixInvalidShapes(node.children) : [{ text: '' }];
            out.push({ ...node, type: 'h3', children });
            continue;
        }

        if (Array.isArray(node.children)) {
            out.push({ ...node, children: fixInvalidShapes(node.children) });
        } else {
            out.push(node);
        }
    }
    return out;
}

export interface DocxImportResult {
    fragment: any[];
    /** Non-fatal conversion notes from mammoth (unsupported styles, etc.). */
    messages: string[];
}

// mammoth reads w:jc into paragraph.alignment but its styleMap has no
// alignment matcher, so aligned paragraphs get a synthetic `align-*` style
// name that DOCX_IMPORT_STYLE_MAP can match. A paragraph that already carries
// a real named style (any styleId — Heading N, Quote, List Paragraph, ...)
// keeps it: overwriting styleId/styleName would shadow that style's OWN
// styleMap mapping (e.g. Quote => blockquote) for a plain aligned `p`.
// Numbered paragraphs are carved out explicitly too, since a doc can apply
// numbering via direct pPr/numPr with no named style at all. Alignment
// fidelity for every carved-out paragraph does not depend on this
// synthetic-class route: applyOoxmlFidelity's raw-XML pass re-attaches
// `align` from `w:jc` straight onto the fragment afterwards (additively), so
// skipping the mammoth-side injection here never loses the align prop.
// Pure paragraph decision behind the alignment styleMap shim — exported for a
// fast unit test (the full docx round-trip in docxAlignStylePreservation.test.ts
// is a ~30s integration test kept skipped in CI). Only synthesises an align-*
// style for an aligned paragraph with NO real style identity or numbering, so a
// styled paragraph (e.g. "Quote") keeps its own styleId for mammoth's styleMap
// to resolve — the RECUR-26249 regression this guards against.
export function alignmentParagraphTransform(paragraph: any): any {
    const textAlign = DOCX_JC_TO_TEXT_ALIGN[paragraph?.alignment ?? ''];
    if (!textAlign || paragraph?.numbering || paragraph?.styleId) return paragraph;
    const styleName = `${DOCX_ALIGN_STYLE_NAME_PREFIX}${textAlign}`;
    return { ...paragraph, styleId: styleName, styleName };
}

function buildAlignmentTransform(mammoth: any) {
    return mammoth.transforms.paragraph(alignmentParagraphTransform);
}

// Rewrite the marker classes the styleMap emitted into the inline styles the
// editor's deserializer parses (backgroundColor mark / block align), then drop
// them so no docx-* class leaks into the document.
function applyDocxMarkerClasses(body: HTMLElement): void {
    body.querySelectorAll('[class]').forEach((el) => {
        for (const cls of Array.from(el.classList)) {
            if (cls.startsWith(DOCX_HIGHLIGHT_CLASS_PREFIX)) {
                const hex = DOCX_HIGHLIGHT_HEX[cls.slice(DOCX_HIGHLIGHT_CLASS_PREFIX.length)];
                if (hex) (el as HTMLElement).style.backgroundColor = hex;
                el.classList.remove(cls);
            } else if (cls.startsWith(DOCX_ALIGN_CLASS_PREFIX)) {
                (el as HTMLElement).style.textAlign = cls.slice(DOCX_ALIGN_CLASS_PREFIX.length);
                el.classList.remove(cls);
            }
        }
        if (!el.getAttribute('class')) el.removeAttribute('class');
    });
}

const RGB_COLOR_RE = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;

// CSSOM normalizes the hex we set in applyDocxMarkerClasses to `rgb(r, g, b)`
// before FontBackgroundColorPlugin reads it back off element.style;
// re-canonicalize to the lowercase hex form the rest of the app uses.
function hexifyBackgroundColors(nodes: any[]): void {
    for (const node of nodes) {
        if (typeof node?.backgroundColor === 'string') {
            const match = RGB_COLOR_RE.exec(node.backgroundColor);
            if (match) {
                node.backgroundColor = `#${match
                    .slice(1, 4)
                    .map((channel) => Number(channel).toString(16).padStart(2, '0'))
                    .join('')}`;
            }
        }
        if (Array.isArray(node?.children)) hexifyBackgroundColors(node.children);
    }
}

function isEmptyParagraph(node: any): boolean {
    if (node?.type !== 'p' || !Array.isArray(node.children)) return false;
    if (Object.keys(node).some((key) => key !== 'type' && key !== 'children')) return false;
    if (node.children.length === 0) return true;
    if (node.children.length !== 1) return false;
    const child = node.children[0];
    return child?.text === '' && Object.keys(child).length === 1;
}

// `ignoreEmptyParagraphs: false` preserves the doc's intentional blank-line
// spacing, but docs routinely end in stray empty paragraphs — trim ALL of
// those (stripNewlinesFromFragment only pops one). Interior empties survive.
function trimTrailingEmptyParagraphs(fragment: any[]): any[] {
    let end = fragment.length;
    while (end > 1 && isEmptyParagraph(fragment[end - 1])) end -= 1;
    return end === fragment.length ? fragment : fragment.slice(0, end);
}

/**
 * Convert a `.docx` File/Blob into a Plate fragment ready for
 * `editor.tf.insertFragment(...)`. Throws if the file isn't a readable docx.
 */
export async function docxFileToFragment(editor: any, file: File | Blob): Promise<DocxImportResult> {
    const arrayBuffer = await file.arrayBuffer();

    // mammoth → Word HTML. `convertToHtml` resolves even for messy docs,
    // collecting any warnings in `result.messages` rather than throwing.
    // Options recover what mammoth's defaults silently drop: underline
    // (ignored unless mapped), w:highlight (only emitted via matchers),
    // empty paragraphs (the doc's blank-line spacing), and w:jc alignment
    // (never emitted without the transform + synthetic style matchers).
    const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
            styleMap: DOCX_IMPORT_STYLE_MAP,
            ignoreEmptyParagraphs: false,
            transformDocument: buildAlignmentTransform(mammoth),
        },
    );
    const html = result?.value ?? '';
    const messages = Array.isArray(result?.messages) ? result.messages.map((m: any) => m?.message ?? String(m)) : [];

    if (!html.trim()) {
        return { fragment: [], messages };
    }

    // Reuse the same cleaning the paste path applies (strips Office cruft,
    // <style> blocks, conditional comments, …).
    const cleaned = sanitizeOfficeHtml(html);
    const parsed = new DOMParser().parseFromString(cleaned, 'text/html');
    const body = parsed?.body;
    if (!body) return { fragment: [], messages };

    // Mirror handlePaste: strip `data-slate-*` markers so Plate runs its normal
    // HTML parse rules instead of treating nodes as "already slate" (which would
    // silently drop block wrappers). mammoth doesn't emit these, but this keeps
    // the two ingestion paths byte-identical and future-proof.
    body.querySelectorAll('[data-slate-node], [data-slate-leaf], [data-slate-string], [data-slate-fragment]').forEach((el) => {
        el.removeAttribute('data-slate-node');
        el.removeAttribute('data-slate-leaf');
        el.removeAttribute('data-slate-string');
        el.removeAttribute('data-slate-fragment');
        el.removeAttribute('data-slate-type');
        el.removeAttribute('data-slate-inline');
        el.removeAttribute('data-slate-void');
        el.removeAttribute('data-slate-marks');
    });

    applyDocxMarkerClasses(body);

    // Run the paste path's list/align prep. Plate plugins' `transformData`
    // hooks (including @platejs/list's <li> handling) only fire through
    // `editor.tf.insertData`; the bare `deserializeHtmlElement` below skips
    // them, so without this prep every <li> parses to a plain `p` and nested
    // item text concatenates into the parent paragraph.
    flattenPastedListItems(body);
    enrichPastedListElements(body, 1);
    enrichPastedAlign(body);

    // Match the paste path: run Plate's whitespace collapsing before the bare
    // element mapper (deserializeHtmlElement skips the collapseWhiteSpace step
    // that the full deserializeHtml dispatcher runs).
    let normalizedBody: HTMLElement = body;
    try {
        normalizedBody = collapseWhiteSpace(body);
    } catch {
        normalizedBody = body;
    }

    const fragment: any = (deserializeHtmlElement as any)(editor, normalizedBody);
    hexifyBackgroundColors(Array.isArray(fragment) ? fragment : []);

    // Fix invalid node types (img→image, h4-h6→h3) before layout conversion.
    const fixed = fixInvalidShapes(Array.isArray(fragment) ? fragment : []);

    // Re-attach the OOXML-only properties mammoth structurally can't emit (font
    // size/color, Google-Docs w:shd highlight, line spacing, real numbering
    // formats). Best-effort + additive — a parse failure returns `fixed`
    // unchanged. Runs on the flat fragment (before column conversion) so
    // paragraph order still lines up with document.xml for text anchoring.
    const enriched = await applyOoxmlFidelity(arrayBuffer, fixed);

    // Word uses tables for column layout (résumé panels, "label | value" grids,
    // empty spacer columns). Convert single-row layout tables into Plate column
    // groups so the side-by-side layout is preserved and editable; genuine data
    // tables are left intact.
    const withColumns = convertLayoutTablesToColumns(enriched);
    return { fragment: trimTrailingEmptyParagraphs(withColumns), messages };
}
